import React, { useRef, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import {
    HIDE_MODAL,
    MODAL_TYPE_LOGGED_OUT,
    MODAL_TYPE_LIMITED_ACCESS,
    MODAL_TYPE_INTERSTITIEL,
    MODAL_TYPE_ADBLOCKER,
    MODAL_TYPE_INFORMATIVE,
    MODAL_TYPE_ALO_DELETE_ACCOUNT,
    MODAL_TYPE_ALO_REACTIVATE_ACCOUNT,
    MODAL_TYPE_DISENROL,
    MODAL_TYPE_DELETE_IBAN,
    MODAL_TYPE_KYC,
    MODAL_TYPE_LOGIN_ERROR
} from '../../services/modal/constants';

import AdBlockerModal from '../adblocker/AdBlockerModal';
import InformativeModal from '../informativemodal/InformativeModal';
import InterstitielModalComponent from '../pages/interstitiel/InterstitielModalComponent';
import AloDeleteAccountModal from '../alo/AloDeleteAccountModal';
import AloReactivateAccountModal from '../alo/AloReactivateAccountModal';
import DisenrolModalComponent from '../authentication/disenrollment/DisenrolModalComponent';
import DeleteIbanModalComponent from '../pages/account/myprofile/DeleteIbanModalComponent';
import LoggedOutModalComponent from '../authentication/loggedout/LoggedOutModal';
import LimitedAccessModal from '../authentication/limitedaccess/LimitedAccessModal';
import LoginErrorModal from '../authentication/loginerror/LoginErrorModal';
import KycModal from '../fraud/kyc/KycModal';

import CloseButtonComponent from '../button/CloseButtonComponent';

import './ModalRoot.scss';

// Chaque nouvelle Modal spécifique doit être ajoutée ici
const MODAL_COMPONENTS = {
    [MODAL_TYPE_LOGGED_OUT]: LoggedOutModalComponent,
    [MODAL_TYPE_LIMITED_ACCESS]: LimitedAccessModal,
    [MODAL_TYPE_INTERSTITIEL]: InterstitielModalComponent,
    [MODAL_TYPE_ADBLOCKER]: AdBlockerModal,
    [MODAL_TYPE_INFORMATIVE]: InformativeModal,
    [MODAL_TYPE_ALO_DELETE_ACCOUNT]: AloDeleteAccountModal,
    [MODAL_TYPE_ALO_REACTIVATE_ACCOUNT]: AloReactivateAccountModal,
    [MODAL_TYPE_DISENROL]: DisenrolModalComponent,
    [MODAL_TYPE_DELETE_IBAN]: DeleteIbanModalComponent,
    [MODAL_TYPE_KYC]: KycModal,
    [MODAL_TYPE_LOGIN_ERROR]: LoginErrorModal
};

// Libellé accessible (aria-label du dialog) par défaut selon le type de modale.
// Résolu depuis l'i18n (state.i18n). Un ariaLabel passé à showModal reste prioritaire
// (ex. cas dynamique de DELETE_IBAN qui dépend de l'IBAN).
const DEFAULT_ARIA_LABELS = {
    [MODAL_TYPE_LOGGED_OUT]: i18n => i18n.page.LOGGED_OUT_MODAL.ACCESSIBILITY.MODAL_LABEL,
    [MODAL_TYPE_LIMITED_ACCESS]: i18n => i18n.page.LIMITED_ACCESS_MODAL.ACCESSIBILITY.MODAL_LABEL,
    [MODAL_TYPE_DISENROL]: i18n => i18n.page.DISENROLLMENT_MODAL.ACCESSIBILITY.MODAL_LABEL,
    [MODAL_TYPE_ADBLOCKER]: i18n => i18n.page.ADBLOCKER_MODAL.WARNING_MESSAGE,
    [MODAL_TYPE_INFORMATIVE]: i18n => i18n.page.INFORMATIVE_MODAL.ACCESSIBILITY.MODAL_LABEL,
    [MODAL_TYPE_KYC]: i18n => i18n.FRAUD.KYC_MODAL.ACCESSIBILITY.MODAL_LABEL,
    [MODAL_TYPE_LOGIN_ERROR]: i18n => i18n.page.LOGIN_ERROR_MODAL.ACCESSIBILITY.MODAL_LABEL
};

const ModalRoot = ({
    modal,
    dispatch,
    location,
    injectCloseButton,
    redirectOnClose,
    redirectToUrl,
    actionOnClose,
    id,
    ariaLabel,
    defaultAriaLabel
}) => {
    const modalRef = useRef(null);

    // Piège à focus générique pour TOUTES les modales (RGAA 12.x) :
    // focus initial dans la modale, boucle Tab/Shift+Tab, restauration du focus à la fermeture.
    useEffect(() => {
        if (!modal.modalType || !modalRef.current) {
            return undefined;
        }

        const modalEl = modalRef.current;
        const FOCUSABLE =
            'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const previouslyFocused = document.activeElement;

        const getFocusable = () =>
            Array.from(modalEl.querySelectorAll(FOCUSABLE)).filter(
                el => !el.disabled && el.offsetParent !== null
            );

        // Focus dans la modale : 1er contrôle hors déclencheur de tooltip (un bouton
        // d'aide ouvrirait l'infobulle au focus). Repli : 1er focusable, sinon le conteneur.
        const focusInModal = () => {
            const items = getFocusable();
            const target = items.find(el => !el.closest('.tooltip')) || items[0] || modalEl;
            target.focus();
        };

        focusInModal();

        const handleKeyDown = e => {
            // Fermeture au clavier (pattern ARIA dialog), uniquement pour les modales
            // fermables (celles qui exposent le bouton de fermeture)
            if (e.key === 'Escape' && injectCloseButton) {
                e.preventDefault();
                dispatch({ type: HIDE_MODAL });
                if (actionOnClose) {
                    actionOnClose();
                }
                return;
            }

            if (e.key !== 'Tab') {
                return;
            }
            const items = getFocusable();
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                last.focus();
                e.preventDefault();
            } else if (!e.shiftKey && document.activeElement === last) {
                first.focus();
                e.preventDefault();
            }
        };

        // Ré-assertion : si le focus s'échappe de la modale (re-rendu, blur passif…),
        // on le ramène à l'intérieur.
        const handleFocusIn = e => {
            if (modalEl.isConnected && !modalEl.contains(e.target)) {
                focusInModal();
            }
        };

        document.addEventListener('keydown', handleKeyDown, false);
        document.addEventListener('focusin', handleFocusIn, false);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, false);
            document.removeEventListener('focusin', handleFocusIn, false);
            if (
                previouslyFocused &&
                previouslyFocused !== document.body &&
                typeof previouslyFocused.focus === 'function'
            ) {
                previouslyFocused.focus();
            }
        };
    }, [modal.modalType]);

    const hideModal = () => {
        dispatch({ type: HIDE_MODAL });
    };

    if (!modal.modalType) {
        return null;
    }

    const SpecificModal = MODAL_COMPONENTS[modal.modalType];
    const isLarge = [MODAL_TYPE_ADBLOCKER].includes(modal.modalType);
    // ariaLabel explicite prioritaire, sinon libellé par défaut selon le type de modale
    const effectiveAriaLabel = ariaLabel || defaultAriaLabel || '';

    const onCloseButton = () => {
        hideModal();
        if (actionOnClose) {
            actionOnClose();
        }
    };

    const renderCloseButton = () => {
        if (injectCloseButton) {
            if (redirectOnClose && !isEmpty(redirectToUrl)) {
                return (
                    <a className="bt-close" href={redirectToUrl} onClick={onCloseButton}>
                        {`Redirection vers ${redirectToUrl}`}
                    </a>
                );
            } else {
                return <CloseButtonComponent onClick={onCloseButton} />;
            }
        }
    };

    const renderBlocker = () => {
        return <div className="blocker" />;
    };

    return (
        <div>
            <div>
                {renderBlocker()}
                <div
                    className={`modal-container flex-container-v flex-center conteneur-mobile ${
                        isLarge ? 'large' : ''
                    }`}
                >
                    <div
                        ref={modalRef}
                        id={id ? id : ''}
                        className="modal"
                        tabIndex="-1"
                        role="dialog"
                        aria-label={effectiveAriaLabel}
                        aria-modal="true"
                    >
                        <SpecificModal {...modal.modalProps} currentPath={location.pathname} />
                        {renderCloseButton()}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        modal: state.modal,
        injectCloseButton: !state.modal.modalProps || !state.modal.modalProps.hideCloseButton,
        location: state.routing.location,
        redirectOnClose: state.modal.modalProps && state.modal.modalProps.redirectOnClose,
        redirectToUrl: state.modal.modalProps && state.modal.modalProps.redirectToUrl,
        actionOnClose: state.modal.modalProps && state.modal.modalProps.actionOnClose,
        id: state.modal.modalProps && state.modal.modalProps.id,
        ariaLabel: state.modal.modalProps && state.modal.modalProps.ariaLabel,
        defaultAriaLabel: DEFAULT_ARIA_LABELS[state.modal.modalType]
            ? DEFAULT_ARIA_LABELS[state.modal.modalType](state.i18n)
            : ''
    };
};

export default connect(mapStateToProps)(ModalRoot);



// WEBPACK FOOTER //
// ./src/components/modal/ModalRoot.js